import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { FileTabsViewer, type FileTreeEntry } from '@localagent/ui';
import type { WorkspaceRoutingClass } from '@localagent/sdk';
import { useWorkspaceFileBrowser } from '../../hooks/useWorkspaceFileBrowser';
import { useWorkspaceNodeActions } from '../../hooks/useWorkspaceNodeActions';
import { useWorkspaceFilesChanged } from '../../hooks/useWorkspaceFilesChanged';
import { useCloudFilesChanged } from '../../hooks/useCloudFilesChanged';
import { useChatPanelLayout } from '../../hooks/useChatPanelLayout';
import { useChatTreeWidthPreference } from '../../hooks/useUserPreferences';
import { useTreeColumnResize } from '../../hooks/useTreeColumnResize';
import { WorkspaceDirectoryTree } from './WorkspaceDirectoryTree';
import { WorkspaceFileContextMenu } from './WorkspaceFileContextMenu';
import { NewFileAction } from './NewFileAction';
import { ChatLayoutControls } from '../layout/ChatLayoutControls';
import { FileHistoryButton } from './FileHistoryButton';

/**
 * Imperative surface ChatView reaches the host-owned file-browser state
 * through (the `useWorkspaceFileBrowser` instance lives HERE, beside the
 * components it feeds). Used for the automatic turn-end refresh trigger
 * — no global store involved.
 */
export interface ChatWorkspaceFileBrowserHandle {
  refreshTree: (opts?: { manual?: boolean }) => Promise<void>;
}

const MIN_PANEL = 220; // px — smallest the file panel may shrink to.
// The chat column keeps at least this much room: when the window can't honor the
// file panel's saved size AND a readable chat, the panel yields (its rendered
// size shrinks) rather than collapsing the chat to a sliver. The persisted size
// is unchanged — only the on-screen size is clamped.
const MIN_CHAT = 360;

/**
 * Layout for a workspace-attached chat: a fixed directory-tree column on the
 * left, then a region holding the chat and the tabbed file viewer arranged per
 * the user's configurable position (left/right/above/below), with an axis-aware
 * resize handle between them. Owns the shared file-browser state (tree ↔ tabs
 * stay in sync) via {@link useWorkspaceFileBrowser}.
 *
 * The Progress/Events dock is NOT here — it stays a sibling in ChatView (files
 * are deliberately not part of the progress tracker).
 */
export function ChatWorkspaceLayout({
  workspaceId,
  workspacePath,
  mode,
  onEntriesDiscovered,
  onNodeDragStart,
  fileBrowserRef,
  header,
  chat,
}: {
  workspaceId: string;
  workspacePath?: string;
  mode?: WorkspaceRoutingClass;
  onEntriesDiscovered?: (entries: FileTreeEntry[]) => void;
  onNodeDragStart?: (entry: FileTreeEntry, e: DragEvent) => void;
  /** Lets the host (ChatView) drive automatic tree refreshes (turn end). */
  fileBrowserRef?: Ref<ChatWorkspaceFileBrowserHandle>;
  /** The chat header bar, rendered FULL-WIDTH above the tree (the tree sits
   *  under it, not beside it). Kept out of the chat body so it can span the
   *  directory-tree column too. */
  header?: ReactNode;
  chat: ReactNode;
}) {
  const fb = useWorkspaceFileBrowser({
    workspaceId,
    workspacePath,
    mode,
    onEntriesDiscovered,
  });
  const { hasDirty, lastOwnSaveAtRef, recheckActiveFile, refreshTree } = fb;
  const nodeActions = useWorkspaceNodeActions({ fb, onOpenFile: fb.openFile });
  const { position, size, setSize, commitSize } = useChatPanelLayout();
  const { chatTreeWidth, setChatTreeWidth } = useChatTreeWidthPreference();
  const treeResize = useTreeColumnResize({
    persistedWidth: chatTreeWidth,
    onCommitWidth: setChatTreeWidth,
  });

  // Exit guard: armed ONLY while a tab is dirty. On
  // web this is the native leave-page prompt; on desktop, Electron routes the
  // blocked unload through the main process's `will-prevent-unload` handler
  // (window-manager.ts), which shows the confirm dialog — a bare renderer
  // beforeunload would silently swallow the window close otherwise.
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requires returnValue to be set for the prompt to appear.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasDirty]);

  // Expose the refresh controller to ChatView (turn-end automatic trigger).
  useImperativeHandle(fileBrowserRef, () => ({ refreshTree }), [refreshTree]);

  // Local watcher trigger: the desktop daemon broadcasts
  // workspace:filesChanged (COLON form, 1 s debounce) after its debounced
  // watcher pass. In ADDITION to the tree refresh, run the fast path:
  // one bounded re-read of the ACTIVE tab (clean+changed → silent reload;
  // dirty+changed → banner) and a cache-bust of non-active clean tabs. Web
  // has no daemon and the hook no-ops there by construction.
  // fb is a fresh object each render; refreshTree/recheckActiveFile are the
  // stable deps.
  const onWatcherFilesChanged = useCallback(() => {
    void recheckActiveFile();
    void refreshTree();
  }, [recheckActiveFile, refreshTree]);
  useWorkspaceFilesChanged({
    workspaceId,
    enabled: mode === 'local',
    onFilesChanged: onWatcherFilesChanged,
  });

  // Cloud push trigger: the Go worker fans
  // workspace.filesChanged (DOT form — bridge method name, relayed by the
  // daemon onto its loopback /events stream) after a mutating file op in the
  // workspace's cloud pod. 3 s trailing debounce + self-echo suppression
  // (skip when our own save landed within the window) live in the hook.
  const onCloudFilesChanged = useCallback(() => {
    void refreshTree();
  }, [refreshTree]);
  useCloudFilesChanged({
    workspaceId,
    enabled: mode === 'cloud',
    lastOwnSaveAtRef,
    onFilesChanged: onCloudFilesChanged,
  });

  const vertical = position === 'top' || position === 'bottom';
  const panelFirst = position === 'left' || position === 'top';

  // Axis-aware resize: compute the panel size from the pointer position within
  // the chat+panel region, honoring which side the panel is on.
  const regionRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Track the chat+panel region's extent so we can clamp the panel's RENDERED
  // size to keep the chat at least MIN_CHAT wide (the min-chat-width guarantee).
  const [regionExtent, setRegionExtent] = useState(0);
  useEffect(() => {
    const el = regionRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setRegionExtent(vertical ? r.height : r.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [vertical]);
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const el = regionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const raw = vertical
        ? panelFirst
          ? e.clientY - rect.top
          : rect.bottom - e.clientY
        : panelFirst
          ? e.clientX - rect.left
          : rect.right - e.clientX;
      const extent = vertical ? rect.height : rect.width;
      // Panel spans [MIN_PANEL, extent - MIN_CHAT] so dragging can never starve
      // the chat below its floor.
      setSize(Math.max(MIN_PANEL, Math.min(extent - MIN_CHAT, raw)));
    };
    const onUp = () => {
      setIsResizing(false);
      commitSize();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = vertical ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, vertical, panelFirst, setSize, commitSize]);

  const handle = (
    <div
      data-testid="file-panel-resize"
      onMouseDown={(e) => {
        e.preventDefault();
        setIsResizing(true);
      }}
      className={`flex-shrink-0 ${vertical ? 'h-1 cursor-row-resize' : 'w-1 cursor-col-resize'} bg-border hover:bg-primary/40 active:bg-primary/60`}
    />
  );

  // Rendered size: the saved size, but never so large that the chat drops below
  // MIN_CHAT (the panel yields when the window is too small — B). The persisted
  // `size` is untouched; this only affects what's drawn.
  const appliedSize =
    regionExtent > 0 ? Math.max(MIN_PANEL, Math.min(size, regionExtent - MIN_CHAT)) : size;

  const panel = (
    <div
      className="flex min-h-0 flex-shrink-0 overflow-hidden"
      style={vertical ? { height: appliedSize } : { width: appliedSize }}
      data-testid="chat-file-panel"
    >
      <FileTabsViewer
        openPaths={fb.openPaths}
        activePath={fb.activePath}
        onActivate={fb.setActive}
        onClose={fb.closeFile}
        read={fb.read}
        activeSizeBytes={fb.activeSize}
        onOpenExternally={fb.openExternal}
        editing={fb.editing}
        toolbar={fb.activePath ? <FileHistoryButton workspaceId={workspaceId} path={fb.activePath} /> : undefined}
      />
    </div>
  );

  // flex-col so the chat (a `flex h-full flex-col` with no width of its own)
  // STRETCHES to the column's full width instead of shrinking to its content —
  // otherwise the composer/messages collapse to ~150px (a flex-row item sizes to
  // content on the main axis).
  const chatCol = <div className="flex min-h-0 min-w-0 flex-1 flex-col">{chat}</div>;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Chat header bar — spans the FULL width, above the directory tree, so the
          tree sits under it rather than beside it. */}
      {header && (
        <div
          className="flex-shrink-0 bg-background px-4 py-3"
          data-testid="chat-header-bar"
        >
          {header}
        </div>
      )}

      {/* Below the header: directory tree (left) then the chat + file panel. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {/* Directory tree — the leftmost column under the header (per design).
            Resizable via a drag handle on the trailing edge, mirroring the
            standalone Files tab's tree (app-shell ADR-c857a: own preference
            field, same `useTreeColumnResize` mechanism). */}
        <div
          className="relative flex-shrink-0 border-r border-border"
          data-testid="chat-tree-column"
          data-resizing={String(treeResize.isResizing)}
          style={{ width: treeResize.size }}
        >
          <div
            data-testid="chat-tree-resize-handle"
            onPointerDown={treeResize.onHandlePointerDown}
            title="Resize tree"
            aria-label="Resize tree"
            className="absolute inset-y-0 right-0 z-10 w-1 cursor-col-resize touch-none select-none hover:bg-primary/40 active:bg-primary/60"
          />
          <WorkspaceDirectoryTree
            rootEntries={fb.rootEntries}
            loadChildren={fb.loadChildren}
            notReady={fb.notReady}
            resume={fb.resume}
            activePath={fb.activePath}
            onOpenFile={fb.openFile}
            onNodeDragStart={onNodeDragStart}
            onNodeContextMenu={nodeActions.openAt}
            onBackgroundContextMenu={nodeActions.openRootAt}
            onRefresh={() => void fb.refreshTree({ manual: true })}
            refreshing={fb.refreshing}
            refreshError={fb.refreshError}
            actionError={nodeActions.actionError}
            refreshRef={fb.treeRefreshRef}
            headerActions={
              <>
                <NewFileAction onCreateFile={(name) => fb.createFile(name, '')} />
                <ChatLayoutControls />
              </>
            }
          />
          <WorkspaceFileContextMenu actions={nodeActions} />
        </div>

        {/* Chat + tabbed file viewer, arranged by the configurable position. */}
        <div
          ref={regionRef}
          className={`flex min-h-0 min-w-0 flex-1 ${vertical ? 'flex-col' : 'flex-row'}`}
        >
          {panelFirst ? (
            <>
              {panel}
              {handle}
              {chatCol}
            </>
          ) : (
            <>
              {chatCol}
              {handle}
              {panel}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
